import { Component, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AssignTrainingService,
  ApiResponse,
  TrainingFileDTO,
  AssignedTraining,
  CreateAssignmentPayload,
} from '../../services/assign-training.service';

type DropdownItem = TrainingFileDTO | string;

interface Dropdown {
  key: string;
  label: string;
  placeholder: string;
  required: boolean;
  multi: boolean;
  items: DropdownItem[];
  selected: DropdownItem[];
  search: string;
  open: boolean;
  loading: boolean;
  displayFn: (item: DropdownItem) => string;
}

@Component({
  selector: 'app-assign-training',
  templateUrl: './assign-training.component.html',
  styleUrls: ['./assign-training.component.css']
})
export class AssignTrainingComponent implements OnInit, OnDestroy {
  selected = 'assign-training';
  private destroy$ = new Subject<void>();

  dropdowns: Dropdown[] = [
    { key: 'trainings', label: 'Select Training', placeholder: 'Select Training', required: true, multi: false, items: [], selected: [], search: '', open: false, loading: false, displayFn: (item) => (item as TrainingFileDTO).fileName ?? '' },
    { key: 'employers', label: 'Select an Employer', placeholder: 'Select an Employer', required: false, multi: true, items: [], selected: [], search: '', open: false, loading: false, displayFn: (item) => item as string },
    { key: 'programs', label: 'Select a Program', placeholder: 'Select a Program', required: false, multi: true, items: [], selected: [], search: '', open: false, loading: false, displayFn: (item) => item as string },
    { key: 'clients', label: 'Select a Client', placeholder: 'Select a Client', required: false, multi: true, items: [], selected: [], search: '', open: false, loading: false, displayFn: (item) => item as string },
    { key: 'jobs', label: 'Select a Job', placeholder: 'Select a Job', required: false, multi: true, items: [], selected: [], search: '', open: false, loading: false, displayFn: (item) => item as string },
  ];

  assignedRows: AssignedTraining[] = [];
  isLoadingTable = false;
  pageNumber = 1;
  pageSize = 10;
  pageSizeOptions = [10, 20, 50, 100];
  totalRecords = 0;
  editingRow: AssignedTraining | null = null;
  editForm = { startDate: '', endDate: '' };
  isSubmitting = false;
  isSavingEdit = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(private assignTrainingService: AssignTrainingService, private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.loadDropdown('trainings', this.assignTrainingService.getTrainingList());
    this.loadDropdown('employers', this.assignTrainingService.getEmployers());
    this.loadDropdown('programs', this.assignTrainingService.getPrograms());
    this.loadDropdown('clients', this.assignTrainingService.getClients());
    this.loadDropdown('jobs', this.assignTrainingService.getJobs());
    this.loadAssignedTrainings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDropdown(key: string, source$: Observable<ApiResponse<DropdownItem[]>>): void {
    const dd = this.getDropdown(key);
    dd.loading = true;
    source$.pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        dd.items = (response.data || [])
          .map(item => (typeof item === 'string' ? item.trim() : item) as DropdownItem)
          .sort((a, b) => dd.displayFn(a).toLowerCase().localeCompare(dd.displayFn(b).toLowerCase()));
        dd.loading = false;
      },
      () => { dd.loading = false; }
    );
  }

  loadAssignedTrainings(): void {
    this.isLoadingTable = true;
    this.assignTrainingService.getAssignedTrainings(this.pageNumber, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.assignedRows = response.data?.assignedTrainings || [];
          this.totalRecords = response.data?.totalCount || 0;
          this.isLoadingTable = false;
        },
        () => { this.isLoadingTable = false; }
      );
  }

  onPageSizeChange(): void {
    this.pageNumber = 1;
    this.loadAssignedTrainings();
  }

  getDropdown(key: string): Dropdown {
    return this.dropdowns.find(d => d.key === key)!;
  }

  filteredItems(dd: Dropdown): DropdownItem[] {
    if (!dd.search) return dd.items;
    const search = dd.search.toLowerCase();
    return dd.items.filter(item => dd.displayFn(item).toLowerCase().includes(search));
  }

  toggleDropdown(dd: Dropdown): void {
    const wasOpen = dd.open;
    this.dropdowns.forEach(d => d.open = false);
    dd.open = !wasOpen;
  }

  toggleItem(dd: Dropdown, item: DropdownItem): void {
    this.errorMessage = null;
    if (dd.multi) {
      const index = dd.selected.indexOf(item);
      if (index > -1) {
        dd.selected.splice(index, 1);
      } else {
        dd.selected.push(item);
      }
    } else {
      dd.selected = dd.selected[0] === item ? [] : [item];
      dd.open = false;
    }
  }

  isItemSelected(dd: Dropdown, item: DropdownItem): boolean {
    return dd.selected.includes(item);
  }

  getLabel(dd: Dropdown): string {
    if (dd.selected.length === 0) return dd.placeholder;
    if (dd.selected.length === 1) return dd.displayFn(dd.selected[0]);
    return `${dd.selected.length} selected`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    this.dropdowns.forEach(d => {
      if (d.open) {
        const panels = this.elementRef.nativeElement.querySelectorAll('.dropdown-container');
        const ddIndex = this.dropdowns.indexOf(d);
        if (!panels[ddIndex] || !panels[ddIndex].contains(target)) {
          d.open = false;
        }
      }
    });
  }

  addToTable(): void {
    this.dropdowns.forEach(d => d.open = false);
    const trainings = this.getDropdown('trainings');
    if (trainings.selected.length === 0) {
      alert('Training must be selected.');
      return;
    }

    const otherDropdowns = this.dropdowns.filter(d => d.key !== 'trainings');
    const hasOtherSelection = otherDropdowns.some(d => d.selected.length > 0);
    if (!hasOtherSelection) {
      alert('At least one of Employer, Program, Client or Job must be selected.');
      return;
    }

    const t = trainings.selected[0] as TrainingFileDTO;
    const employers = [...this.getDropdown('employers').selected] as string[];
    const programs = [...this.getDropdown('programs').selected] as string[];
    const clients = [...this.getDropdown('clients').selected] as string[];
    const jobs = [...this.getDropdown('jobs').selected] as string[];

    const payload: CreateAssignmentPayload = {
      trainingFileId: t.id!,
      trainingFileName: t.fileName ?? undefined,
      employers,
      programs,
      clientIds: clients.map(c => { const m = c.match(/\[(\d+)\]$/); return m ? parseInt(m[1], 10) : 0; }),
      clientNames: clients,
      jobs,
    };

    this.isSubmitting = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.assignTrainingService.createAssignment(payload).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.dropdowns.forEach(d => d.selected = []);
        this.successMessage = 'Training assignment created successfully.';
        this.isSubmitting = false;
        this.pageNumber = 1;
        this.loadAssignedTrainings();
      },
      () => {
        this.errorMessage = 'Failed to create training assignment. Please try again.';
        this.isSubmitting = false;
      }
    );
  }

  today = new Date().toISOString().split('T')[0];
  editError: string | null = null;

  private originalEditForm = { startDate: '', endDate: '' };

  editRow(row: AssignedTraining): void {
    this.editingRow = row;
    this.editForm = {
      startDate: row.startDate ? row.startDate.split('T')[0] : '',
      endDate: row.endDate ? row.endDate.split('T')[0] : '',
    };
    this.originalEditForm = { ...this.editForm };
    this.editError = null;
  }

  get hasEditChanges(): boolean {
    return this.editForm.startDate !== this.originalEditForm.startDate
      || this.editForm.endDate !== this.originalEditForm.endDate;
  }

  get canSaveEdit(): boolean {
    return !!this.editForm.startDate && this.hasEditChanges;
  }

  get minEndDate(): string {
    if (!this.editForm.startDate) return this.today;
    const next = new Date(this.editForm.startDate);
    next.setDate(next.getDate() + 1);
    return next.toISOString().split('T')[0];
  }

  onStartDateChange(): void {
    if (this.editForm.endDate && this.editForm.startDate && this.editForm.endDate <= this.editForm.startDate) {
      this.editForm.endDate = '';
    }
  }

  private validateEditForm(): string | null {
    if (!this.editForm.startDate) return 'Start Date is required.';
    if (this.editForm.startDate < this.today) return 'Start Date cannot be before today.';
    if (this.editForm.endDate && this.editForm.endDate <= this.editForm.startDate) return 'End Date must be after Start Date.';
    return null;
  }

  saveEdit(): void {
    if (!this.editingRow) return;

    this.editError = this.validateEditForm();
    if (this.editError) return;

    this.isSavingEdit = true;
    const startDate = this.editForm.startDate || null;
    const endDate = this.editForm.endDate || null;

    this.assignTrainingService.updateAssignTraining(this.editingRow.id, startDate, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.editingRow!.startDate = this.editForm.startDate;
          this.editingRow!.endDate = this.editForm.endDate;
          this.editingRow = null;
          this.isSavingEdit = false;
          this.successMessage = 'Training assignment updated successfully.';
        },
        () => {
          this.editError = 'Failed to update training assignment. Please try again.';
          this.isSavingEdit = false;
        }
      );
  }

  cancelEdit(): void {
    if (this.hasEditChanges && !confirm('You have unsaved changes. Are you sure you want to cancel?')) return;
    this.editingRow = null;
  }

  pendingDeleteRow: AssignedTraining | null = null;

  confirmDelete(row: AssignedTraining): void {
    this.pendingDeleteRow = row;
  }

  cancelDelete(): void {
    this.pendingDeleteRow = null;
  }

  deleteRow(): void {
    if (!this.pendingDeleteRow) return;
    const id = this.pendingDeleteRow.id;
    this.pendingDeleteRow = null;
    this.assignTrainingService.deleteAssignTraining(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.successMessage = 'Training assignment deleted successfully.';
          this.loadAssignedTrainings();
        },
        () => {
          this.errorMessage = 'Failed to delete training assignment. Please try again.';
        }
      );
  }


}
